import { useEffect } from "react";
import { Phone, CheckCircle, Briefcase, Building, Route, Zap, Leaf, Droplet, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { Link } from "react-router-dom";

export default function CommercialPage() {
  
  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="Commercial Property Searches - Tailored & Efficient"
        description="Expert commercial property searches tailored to your transaction. Environmental, flood risk, planning, transport, and utility reports for commercial properties."
        keywords="commercial property searches, commercial environmental searches, commercial flood risk, commercial property due diligence"
        canonicalUrl="/commercial"
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "Commercial" }])}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-32 bg-wl-navy overflow-hidden">
        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/commercial-header.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>
        
        {/* Single overlay for darkening */}
        <div className="absolute inset-0 bg-wl-navy/80"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Expert Searches For<br />
              <span className="text-green-400">Commercial Properties</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Tailored search packages for every commercial transaction.
              <br />
              Save time and money with our expert-led approach.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="bg-wl-green hover:bg-wl-green/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Request a Consultation
              </Link>
              <a 
                href="tel:01923620240"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 backdrop-blur-sm flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call 01923 620240/41
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              The Whitefield Advantage for Commercial Transactions
            </h2>
            <p className="text-xl text-wl-gray max-w-4xl mx-auto">
              Commercial property transactions require a unique level of due diligence. Our expertise lies in identifying the precise searches needed for your specific transaction, ensuring you don't waste time or money on unnecessary reports.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg border-l-4 border-wl-blue scroll-animate">
                <Briefcase className="w-10 h-10 text-wl-blue mb-4" />
              <h3 className="font-bold text-wl-navy text-xl mb-2">Specialised Services</h3>
              <p className="text-wl-gray">We analyze the property type and location to delivery a tailored search package around Commercial EPC, Fire Risk, and Asbestos Surveys nationwide.</p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg border-l-4 border-wl-green scroll-animate">
              <Zap className="w-10 h-10 text-wl-green mb-4" />
              <h3 className="font-bold text-wl-navy text-xl mb-2">Efficient & Cost-Effective</h3>
              <p className="text-wl-gray">Our targeted approach means you only pay for the essential searches, preventing delays and reducing overall transaction costs.</p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg border-l-4 border-wl-navy scroll-animate">
                <Building className="w-10 h-10 text-wl-navy mb-4" />
                <h3 className="font-bold text-wl-navy text-xl mb-2">Unmatched Expertise</h3>
              <p className="text-wl-gray">With over 20 years of experience, we have an unrivalled understanding of the complexities of commercial property searches across the UK.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Reports Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              Comprehensive Commercial Reports
            </h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              We provide a full suite of commercial searches to address every potential risk, from environmental contamination to infrastructure projects.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Environmental Reports */}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Environmental</h3>
              <p className="text-wl-gray text-sm">Site-specific contamination risk assessments, including historical land use, landfill data, and potential liabilities under the Environmental Protection Act.</p>
            </div>
            
            {/* Flood Risk */}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Droplet className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Flood Risk</h3>
              <p className="text-wl-gray text-sm">Comprehensive flood risk analysis for commercial properties, covering surface water, groundwater, and river flooding, plus insurance availability.</p>
            </div>
            
            {/* Planning */}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Planning</h3>
              <p className="text-wl-gray text-sm">Detailed reports on planning applications, land use designations, and local development policies that could impact the property's value and use.</p>
            </div>

            {/* Transport & HS2 */}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Route className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Transport & HS2</h3>
              <p className="text-wl-gray text-sm">Analysis of major transport projects, including HS2, Crossrail, and other railway or road schemes that may affect the property.</p>
            </div>
            
            {/* Energy & Infrastructure */}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Energy & Infrastructure</h3>
              <p className="text-wl-gray text-sm">Screening for energy and infrastructure projects such as fracking, wind farms, and solar farms within the vicinity of the property.</p>
            </div>

            {/* Utilities */}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Utilities</h3>
              <p className="text-wl-gray text-sm">Reports on the location of utility assets, including gas and oil pipelines, and telecom cables, to identify any potential development constraints.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-wl-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center scroll-animate">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Streamline Your Workflow?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Contact us today for a no-obligation consultation on your search requirements. 
            Let our expertise save you time and ensure complete due diligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="bg-wl-green hover:bg-wl-green/90 text-white px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Request a Quote
            </Link>
            <a 
              href="tel:01923620240"
              className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 backdrop-blur-sm flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Speak to Our Team
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}